import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Typography, List, ListItem, ListItemIcon, ListItemText, Checkbox } from '@mui/material';
import { useCreateInvoiceItem } from '../../../hooks/invoice/useCreateInvoiceItem';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../../hooks/services/useServices';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import { useHomeFab } from '../../../context/HomeFabContext';
import DefaultServicesSeederDialog from '../../services/modals/DefaultServicesSeederDialog';
import ServiceForm from '../../services/forms/ServiceForm';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InvoiceServicesPriceListForm({
  invoice = null,
  onInvoiceItemSaveSuccess = null,
  open,
  onClose,
}) {
  
  const { t } = useTranslation("common");
  const { setDisableHomeFab } = useHomeFab();

  const [seederDialogOpen, setSeederDialogOpen] =  React.useState(false);

  const onEmptyData = () => {
    setSeederDialogOpen(true);
    setDisableHomeFab(true);
  };

  const onSeederDialogDone = () => {
    resetParent();
    setDisableHomeFab(false);
  };

  const { services, loading, loadMore, searchFunction, resetParent } = useServices(open, 1, 100, onEmptyData);

  const [formOpen, setFormOpen] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState(null);

  // Track selected transaction IDs
  const [items, setItems] = React.useState([]);

  const { create } = useCreateInvoiceItem(onInvoiceItemSaveSuccess);
  const { showSnackbar } = useSnackbar();

  React.useEffect(() => {
    // reset field when there are changes (e.g. when opening edit)
    setItems([]);
  }, [open]);

  const handleEdit = (service) => {
    setSelectedService(service);
    setFormOpen(true);
  };

  const handleSave = async () => {
    try {
      const selectedItems = services?.data
        ?.filter((t) => items.includes(t.id))
        ?.map((service) => ({
          invoice_id: invoice.id,
          name: service.name,
          quantity: 1,
          unit_price: service.price,
        }));
      await create(selectedItems);
      showSnackbar(t("invoice.items.messages.create_success"), "success");
      setItems([]);
      onClose();
    } catch (err) {
      console.log("err.response", err.response);
      showSnackbar(t("invoice.items.messages.save_fail"), "error");
    }
  };

  const handleCancel = () => {
    setItems([]);
    onClose();
  };

  return (
    <>
    <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
      <DialogTitle>Add Items From Price List</DialogTitle>
      <DialogContent sx={{minHeight: 200}} dividers>
        <List>
          {services?.data?.map((service) => {
            const isChecked = items.includes(service.id);

            const handleCheckboxChange = (event) => {
              if (event.target.checked) {
                setItems((prev) => [...prev, service.id]);
              } else {
                setItems((prev) =>
                  prev.filter((id) => id !== service.id)
                );
              }
            };

            if (service.disabled) {
              return null;
            }
            return (
              <ListItem
                key={"service" + service.id}
                sx={{ py: 0 }}
                alignItems="flex-start"
              >
                <ListItemIcon>
                  <Checkbox
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                  />
                </ListItemIcon>
                <ListItemText
                  onClick={() => {if (userHasAccess(SUB_MODULES.SERVICES_PRICE_LIST, 2)) handleEdit(service)}}
                  primary={
                    <Typography variant="body1" component="span" sx={{ maxWidth: "90%" }}>
                      {service.name}
                    </Typography>
                  }
                  secondary={
                    <>
                    <Typography variant="body2" component="span" sx={{ my: 0, maxWidth: "90%" }}>
                      {`${service.description}`}
                    </Typography>
                    <Typography variant="body1" component="span" sx={{ my: 0, maxWidth: "90%" }}>
                      {`${numberToCurrencyFormat(service.price)}`}
                    </Typography>
                    </>
                  }
                />
              </ListItem>
            );
          })}
          {services?.data.length === 0 ?
          <ListItem sx={{ justifyContent: "center" }}>
            <Typography component="span" variant="body2" color="text.primary">
              no services found
            </Typography>
          </ListItem>
          : null }
        </List>
      </DialogContent>

      <DialogActions>
        <Button disabled={items.length === 0} onClick={handleSave}>Add Selected Items</Button>
        <div style={{flex: '1 0 0'}} />
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
    <DefaultServicesSeederDialog
      open={seederDialogOpen}
      setOpen={setSeederDialogOpen} 
      successCallback={onSeederDialogDone}
    />
    <ServiceForm
      open={formOpen}
      onClose={() => { setFormOpen(false)} }
      mode={'edit'} 
      service={selectedService}
      successCallback={resetParent}
    />
    </>
  );
}